// src/features/dashboard/pages/EmployeeProductivityPage.tsx

import {
  useEffect,
  useRef,
} from 'react';

import {
  useLoaderContext,
} from '../../../../components/Loader/LoaderContext';

import DashboardHeader from '../../components/DashboardHeader';

import ProductivityHeader from '../../components/EmployeeProductivity/ProductivityHeader';

import ProductivitySummaryCards from '../../components/EmployeeProductivity/ProductivitySummaryCards';

import HourlyProductivityChart from '../../components/EmployeeProductivity/HourlyProductivityChart';

import TimeClassificationChart from '../../components/EmployeeProductivity/TimeClassificationChart';

import ProductivityTrendChart from '../../components/EmployeeProductivity/ProductivityTrendChart';

import MeVsTeamProductivityChart from '../../components/EmployeeProductivity/MeVsTeamProductivityChart';

import ApplicationDetailTable from '../../components/EmployeeProductivity/ApplicationDetailTable';

import MonthlyProductivityTrendChart from '../../components/EmployeeProductivity/MonthlyProductivityTrendChart';

import {
  useEmployeeProductivity,
} from '../../hooks/Employee/useEmployeeProductivity';


/* ============================================================================
 * BROWSER ZOOM AUTO REFRESH
 *
 * IMPORTANT
 *
 * Chrome browser zoom can change:
 *
 *   80%
 *   90%
 *   100%
 *   110%
 *   125%
 *   150%
 *
 * without doing a normal browser refresh.
 *
 * React/Tailwind may therefore continue rendering using the previous
 * calculated layout until another render/reflow happens.
 *
 * This hook detects the browser zoom change and automatically reloads
 * the page.
 *
 * It does NOT reload because of normal page resize.
 *
 * It reloads ONLY when devicePixelRatio changes.
 * ========================================================================== */

const useBrowserZoomAutoRefresh = () => {

  const initialPixelRatio =
    useRef<number>(
      window.devicePixelRatio,
    );

  const reloadStarted =
    useRef<boolean>(
      false,
    );


  useEffect(() => {

    /*
     * Store the current browser zoom/device pixel ratio.
     *
     * Example:
     *
     * 80%  -> 0.8
     * 100% -> 1
     * 125% -> 1.25
     *
     * The exact value can vary depending on OS/browser display scaling,
     * therefore we compare the current value with the value captured
     * when this page was loaded.
     */

    initialPixelRatio.current =
      window.devicePixelRatio;


    /* ========================================================================
     * GET CURRENT PIXEL RATIO
     * ====================================================================== */

    const getCurrentPixelRatio =
      (): number => {

        return window.devicePixelRatio;
      };


    /* ========================================================================
     * RELOAD PAGE
     * ====================================================================== */

    const reloadForZoomChange =
      () => {

        /*
         * Prevent multiple reloads.
         *
         * Chrome can fire:
         *
         * resize
         * visualViewport resize
         * media-query change
         *
         * at almost the same time.
         */

        if (
          reloadStarted.current
        ) {

          return;
        }


        reloadStarted.current =
          true;


        console.log(
          '================================================',
        );

        console.log(
          'BROWSER ZOOM CHANGED',
        );

        console.log(
          'Previous devicePixelRatio:',
          initialPixelRatio.current,
        );

        console.log(
          'Current devicePixelRatio:',
          getCurrentPixelRatio(),
        );

        console.log(
          'Reloading Employee Productivity Page...',
        );

        console.log(
          '================================================',
        );


        /*
         * Wait briefly so Chrome can finish applying the new zoom.
         */

        window.setTimeout(
          () => {

            window.location.reload();

          },
          150,
        );
      };


    /* ========================================================================
     * CHECK DEVICE PIXEL RATIO
     * ====================================================================== */

    const checkBrowserZoom =
      () => {

        if (
          reloadStarted.current
        ) {

          return;
        }


        const currentPixelRatio =
          getCurrentPixelRatio();


        const previousPixelRatio =
          initialPixelRatio.current;


        /*
         * Browser zoom changed.
         */

        if (
          currentPixelRatio !==
          previousPixelRatio
        ) {

          reloadForZoomChange();

        }

      };


    /* ========================================================================
     * RESIZE EVENT
     *
     * Chrome often fires this when browser zoom changes.
     * ====================================================================== */

    const handleWindowResize =
      () => {

        checkBrowserZoom();

      };


    window.addEventListener(
      'resize',
      handleWindowResize,
      {
        passive: true,
      },
    );


    /* ========================================================================
     * VISUAL VIEWPORT RESIZE
     *
     * Chrome can update visualViewport separately from window resize.
     * ====================================================================== */

    const visualViewport =
      window.visualViewport;


    if (
      visualViewport
    ) {

      visualViewport.addEventListener(
        'resize',
        handleWindowResize,
      );
    }


    /* ========================================================================
     * MEDIA QUERY RESOLUTION LISTENER
     *
     * This gives us another reliable way to detect a devicePixelRatio change.
     *
     * Example:
     *
     * window.devicePixelRatio = 1.25
     *
     * becomes:
     *
     * window.devicePixelRatio = 1
     *
     * when Chrome zoom changes.
     * ====================================================================== */

    let resolutionMediaQuery:
      MediaQueryList | null =
      null;


    let handleResolutionChange:
      (() => void) | null =
      null;


    const createResolutionMediaQuery =
      () => {

        const currentPixelRatio =
          window.devicePixelRatio;


        resolutionMediaQuery =
          window.matchMedia(
            `(resolution: ${currentPixelRatio}dppx)`,
          );


        handleResolutionChange =
          () => {

            checkBrowserZoom();

          };


        if (
          resolutionMediaQuery.addEventListener
        ) {

          resolutionMediaQuery.addEventListener(
            'change',
            handleResolutionChange,
          );

        } else {

          /*
           * Older browser fallback.
           */

          resolutionMediaQuery.addListener(
            handleResolutionChange,
          );
        }

      };


    createResolutionMediaQuery();


    /* ========================================================================
     * POLLING FALLBACK
     *
     * This is the final safety net.
     *
     * Some Chrome/Edge combinations do not consistently trigger resize
     * or media-query events for every zoom transition.
     *
     * We therefore check devicePixelRatio every 100ms.
     *
     * IMPORTANT:
     *
     * This does NOT reload every 100ms.
     *
     * It reloads only when:
     *
     * currentPixelRatio !== initialPixelRatio
     * ====================================================================== */

    const zoomCheckInterval =
      window.setInterval(
        checkBrowserZoom,
        100,
      );


    /* ========================================================================
     * INITIAL CHECK
     * ====================================================================== */

    checkBrowserZoom();


    /* ========================================================================
     * CLEANUP
     * ====================================================================== */

    return () => {

      window.removeEventListener(
        'resize',
        handleWindowResize,
      );


      if (
        visualViewport
      ) {

        visualViewport.removeEventListener(
          'resize',
          handleWindowResize,
        );
      }


      window.clearInterval(
        zoomCheckInterval,
      );


      if (
        resolutionMediaQuery &&
        handleResolutionChange
      ) {

        if (
          resolutionMediaQuery.removeEventListener
        ) {

          resolutionMediaQuery.removeEventListener(
            'change',
            handleResolutionChange,
          );

        } else {

          /*
           * Older browser fallback.
           */

          resolutionMediaQuery.removeListener(
            handleResolutionChange,
          );
        }

      }

    };

  }, []);
};


/* ============================================================================
 * LOADER
 * ========================================================================== */

const EmployeeProductivityLoader = () => {

  return (

    <div
      className="
        flex
        min-h-[560px]
        w-full
        flex-col
        items-center
        justify-center
        rounded-[12px]
        bg-[#F8F9FB]
      "
    >

      {/* ================================================================
          SPINNER
      ================================================================ */}

      <div
        className="
          h-[40px]
          w-[40px]
          animate-spin
          rounded-full
          border-[4px]
          border-[#E4E7EC]
          border-t-[#2563EB]
        "
      />


      {/* ================================================================
          LOADER TITLE
      ================================================================ */}

      <p
        className="
          mt-[12px]
          text-[13px]
          font-medium
          text-[#475467]
        "
      >
        Loading Productivity...
      </p>


      {/* ================================================================
          LOADER DESCRIPTION
      ================================================================ */}

      <p
        className="
          mt-[4px]
          text-[11px]
          text-[#98A2B3]
        "
      >
        Fetching your latest productivity data
      </p>

    </div>
  );
};


/* ============================================================================
 * PAGE
 * ========================================================================== */

const EmployeeProductivityPage = () => {

  /* ==========================================================================
   * BROWSER ZOOM AUTO REFRESH
   *
   * This must be inside the page component.
   *
   * When browser zoom changes:
   *
   * 80%
   *   ↓
   * 100%
   *   ↓
   * page automatically reloads
   *
   * 100%
   *   ↓
   * 125%
   *   ↓
   * page automatically reloads
   *
   * 125%
   *   ↓
   * 80%
   *   ↓
   * page automatically reloads
   * ======================================================================== */

  useBrowserZoomAutoRefresh();


  /* ==========================================================================
   * GLOBAL LOADER
   * ======================================================================== */

  const {
    hideLoader,
  } = useLoaderContext();


  /* ==========================================================================
   * PRODUCTIVITY DATA
   * ======================================================================== */

  const {
    loading,

    header,

    hourlyHeader,

    summaryCards,

    hourlyProductivity,

    timeClassification,

    productivityTrend,

    meVsTeam,

    applicationDetails,

    applicationDetailSummary,

    monthlyProductivityTrend,

    productivityTarget,

  } =
    useEmployeeProductivity();


  /* ==========================================================================
   * HIDE GLOBAL LOADER
   * ======================================================================== */

  useEffect(() => {

    if (
      !loading
    ) {

      const timer =
        window.setTimeout(
          () => {

            hideLoader();

          },
          300,
        );


      return () => {

        window.clearTimeout(
          timer,
        );

      };
    }


    return undefined;

  }, [
    loading,
    hideLoader,
  ]);


  /* ==========================================================================
   * PAGE
   * ======================================================================== */

  return (

    <div
      className="
        min-h-[100dvh]
        w-full
        min-w-0
        overflow-x-hidden

        bg-[#F5F7FB]
      "
    >

      {/* ======================================================================
          DASHBOARD HEADER
      ====================================================================== */}

      <div
        className="
          w-full
          min-w-0

          bg-[#F5F7FB]

          [&_.mb-5]:!mb-1
        "
      >

        <div
          className="
            mx-auto
            box-border
            w-full
            min-w-0
            max-w-[1600px]

            bg-transparent
          "
        >

          <DashboardHeader
            showBackButton
            breadcrumbs={[
              {
                label:
                  'Work Vista',

                path:
                  '/',
              },

              {
                label:
                  'My Dashboard',

                path:
                  '/employee-dashboard',
              },

              {
                label:
                  'Productivity',
              },
            ]}
          />

        </div>

      </div>


      {/* ======================================================================
          MAIN CONTENT
      ====================================================================== */}

      <main
        className="
          w-full
          min-w-0
          overflow-x-hidden

          bg-[#F5F7FB]

          px-3
          pb-5
          pt-0

          sm:px-4
          sm:pb-6
          sm:pt-0

          lg:px-5
          lg:pt-0
        "
      >

        {/* ====================================================================
            PRODUCTIVITY MAIN CARD
        ==================================================================== */}

        <div
          className="
            mx-auto
            box-border

            w-full
            min-w-0
            max-w-[1600px]

            rounded-[16px]

            border
            border-[#D0D5DD]

            bg-[#F8F9FB]

            p-3

            sm:p-4

            lg:p-[18px]
          "
        >

          {/* ==================================================================
              LOADING
          ================================================================== */}

          {loading ? (

            <EmployeeProductivityLoader />

          ) : (

            <>

              {/* ==============================================================
                  PRODUCTIVITY HEADER
              ============================================================== */}

              <section
                className="
                  block
                  w-full
                  min-w-0
                  max-w-full
                  overflow-hidden
                "
              >

                <ProductivityHeader
                  header={
                    header
                  }
                />

              </section>


              {/* ==============================================================
                  PRODUCTIVITY SUMMARY CARDS
              ============================================================== */}

              <section
                className="
                  mt-4

                  block

                  w-full
                  min-w-0
                  max-w-full

                  overflow-hidden
                "
              >

                <ProductivitySummaryCards
                  cards={
                    summaryCards
                  }
                />

              </section>


              {/* ==============================================================
                  HOURLY PRODUCTIVITY
                  +
                  TIME CLASSIFICATION

                  SIDE BY SIDE
              ============================================================== */}

              <section
                className="
                  mt-6

                  grid

                  w-full
                  min-w-0
                  max-w-full

                  grid-cols-1

                  gap-4

                  sm:gap-5

                  lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]

                  lg:gap-6
                "
              >

                {/* ============================================================
                    HOURLY PRODUCTIVITY
                ============================================================ */}

                <div
                  className="
                    block
                    w-full
                    min-w-0
                    max-w-full
                    overflow-hidden
                  "
                >

                  <HourlyProductivityChart
                    data={
                      hourlyProductivity
                    }

                    header={
                      hourlyHeader
                    }
                  />

                </div>


                {/* ============================================================
                    TIME CLASSIFICATION
                ============================================================ */}

                <div
                  className="
                    block
                    w-full
                    min-w-0
                    max-w-full
                    overflow-hidden
                  "
                >

                  <TimeClassificationChart
                    data={
                      timeClassification
                    }
                  />

                </div>

              </section>


              {/* ==============================================================
                  PRODUCTIVITY TREND
                  +
                  ME VS TEAM

                  SIDE BY SIDE
              ============================================================== */}

              <section
                className="
                  mt-6

                  grid

                  w-full
                  min-w-0
                  max-w-full

                  grid-cols-1

                  gap-4

                  sm:gap-5

                  lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]

                  lg:gap-6
                "
              >

                {/* ============================================================
                    PRODUCTIVITY TREND
                ============================================================ */}

                <div
                  className="
                    block
                    w-full
                    min-w-0
                    max-w-full
                    overflow-hidden
                  "
                >

                  <ProductivityTrendChart
                    data={
                      productivityTrend
                    }

                    todayProductivity={
                      header.todayProductivity
                    }
                  />

                </div>


                {/* ============================================================
                    ME VS TEAM PRODUCTIVITY
                ============================================================ */}

                <div
                  className="
                    block
                    w-full
                    min-w-0
                    max-w-full
                    overflow-hidden
                  "
                >

                  <MeVsTeamProductivityChart
                    data={
                      meVsTeam
                    }
                  />

                </div>

              </section>


              {/* ==============================================================
                  APPLICATION DETAIL
                  +
                  MONTHLY PRODUCTIVITY

                  SIDE BY SIDE
              ============================================================== */}

              <section
                className="
                  mt-6

                  grid

                  w-full
                  min-w-0
                  max-w-full

                  grid-cols-1

                  gap-4

                  sm:gap-5

                  lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]

                  lg:items-start

                  lg:gap-6
                "
              >

                {/* ============================================================
                    APPLICATION DETAIL TABLE
                ============================================================ */}

                <div
                  className="
                    block
                    w-full
                    min-w-0
                    max-w-full
                    overflow-hidden
                  "
                >

                  <ApplicationDetailTable
                    data={
                      applicationDetails
                    }

                    summary={
                      applicationDetailSummary
                    }
                  />

                </div>


                {/* ============================================================
                    MONTHLY PRODUCTIVITY TREND
                ============================================================ */}

                <div
                  className="
                    block
                    w-full
                    min-w-0
                    max-w-full
                    overflow-hidden
                  "
                >

                  <MonthlyProductivityTrendChart
                    data={
                      monthlyProductivityTrend
                    }

                    target={
                      productivityTarget
                    }
                  />

                </div>

              </section>

            </>

          )}

        </div>

      </main>

    </div>
  );
};


export default EmployeeProductivityPage;